/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-07-26 10:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Layout, Card, TextStyle } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import LoadingBar from 'react-top-loading-bar';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import MainMenu from 'src/components/containers/main-menu';
import TitleHelp from 'src/components/views/common/title-help';
import { Switch } from 'src/components/views/common/switch';
import RecommendationItem from './recommendation-item';
import ConfirmDisconnectModal from './confirm-disconnect-modal';
import PreviewProductsModal from './preview-products-modal';

import { URL } from 'src/constants/links';
import { KLAVIYO_RECOMMENDATION_TYPE } from 'src/constants/klaviyo';
import EventComingSoon from 'assets/images/klaviyo/event_comingsoon.png';
import ReviewComingSoon from 'assets/images/klaviyo/review_comingsoon.png';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = { isConnected: boolean, isConfirmDisconnect: boolean };

class KlaviyoIntegration extends Component<ComposedProps, State> {
  state = {
    isConnected: true,
    isConfirmDisconnect: false,
    isShowPreview: false,
    selectedRecommendationType: null
  };

  componentDidMount() {}

  handleGoBack = () => {
    history.push(routes.integration.view());
  };

  handleToggleConnect = () => {
    this.setState({
      isConnected: !this.state.isConnected
    });
  };

  handleCheckShowConfirm = () => {
    if (this.state.isConnected) {
      this.setState({
        isConfirmDisconnect: true
      });
    } else {
      this.handleToggleConnect();
    }
  };

  handleCloseConfirm = () => {
    this.setState({
      isConfirmDisconnect: false
    });
  };

  handleDisconnect = () => {};

  handleShowPreview = (type: string) => {
    this.setState({
      isShowPreview: true,
      selectedRecommendationType: type
    });
  };

  handleClosePreview = () => {
    this.setState({
      isShowPreview: false
    });
  };

  render() {
    const { i18n, selectedPlan } = this.props;
    const { isConnected, isConfirmDisconnect, isShowPreview, selectedRecommendationType } =
      this.state;

    return (
      <div className="BC-Page BC-Page__KlaviyoIntegration">
        <LoadingBar color="rgba(0,126,98,1)" shadow={true} ref={e => (this.loadingBar = e)} />
        <MainMenu menuId="menu-integration-third-party" />
        <Page
          title={i18n.translate('AdminHTML.KlaviyoIntegration.Main.pageTitle')}
          breadcrumbs={[
            {
              content: '',
              onAction: this.handleGoBack
            }
          ]}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['ShopifyInventory']} />

          <div className="klaviyo-container">
            <Layout>
              <Layout.Section>
                <div className="klaviyo-row">
                  <div className="klaviyo-col-title">
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.connect_Title')}
                        </TextStyle>
                      </p>
                      <p>
                        <TextStyle variation="subdued">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.connect_Description')}
                        </TextStyle>
                      </p>
                    </div>
                  </div>
                  <div className="klaviyo-col-content">
                    <Card>
                      <div className="card-inner card-inner--column">
                        <Switch
                          id="developer-switch"
                          value={isConnected}
                          onChange={this.handleCheckShowConfirm}
                          label={i18n.translate(
                            'AdminHTML.KlaviyoIntegration.Main.connect_BlockTitle'
                          )}
                        />
                        <p style={{ paddingLeft: 50, marginTop: 4 }}>
                          <TextStyle variation="subdued">
                            {i18n.translate(
                              'AdminHTML.KlaviyoIntegration.Main.connect_BlockDescription'
                            )}
                          </TextStyle>
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>

                <hr className="divider" />

                <div className="klaviyo-row" style={{ marginBottom: 24 }}>
                  <div className="klaviyo-col-title">
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.recommendation_Title')}
                        </TextStyle>
                      </p>
                      <p>
                        <TextStyle variation="subdued">
                          {i18n.translate(
                            'AdminHTML.KlaviyoIntegration.Main.recommendation_Description'
                          )}
                        </TextStyle>
                      </p>
                    </div>
                  </div>
                  <div className="klaviyo-col-content">
                    <Card>
                      <div className="card-inner card-inner--column">
                        <p style={{ marginBottom: 4 }}>
                          {i18n.translate(
                            'AdminHTML.KlaviyoIntegration.Main.recommendation_BlockTitle'
                          )}
                        </p>

                        {KLAVIYO_RECOMMENDATION_TYPE.filter(item => {
                          return item.plans.some(plan => selectedPlan.includes(plan));
                        }).map((item, index) => (
                          <RecommendationItem
                            key={index}
                            title={item.title}
                            description={item.description}
                            isBadgeAI={item.isBadgeAI}
                            type={item.type}
                            onPreview={this.handleShowPreview}
                          />
                        ))}
                      </div>
                    </Card>
                  </div>
                </div>

                <div className="klaviyo-row">
                  <div className="klaviyo-col-title">
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.event_Title')}
                        </TextStyle>
                      </p>
                      <p>
                        <TextStyle variation="subdued">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.event_Description')}
                        </TextStyle>
                      </p>
                    </div>
                  </div>
                  <div className="klaviyo-col-content">
                    <Card>
                      <div className="card-inner card-inner--center">
                        <img src={EventComingSoon} alt="event" />
                        <p className="text-coming-soon">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Common.commingSoon')}
                        </p>
                        <p>
                          <TextStyle variation="subdued">
                            {i18n.translate('AdminHTML.KlaviyoIntegration.Common.stayTunes')}
                          </TextStyle>
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>

                <hr className="divider" />

                <div className="klaviyo-row">
                  <div className="klaviyo-col-title">
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.review_Title')}
                        </TextStyle>
                      </p>
                      <p>
                        <TextStyle variation="subdued">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Main.review_Description')}
                        </TextStyle>
                      </p>
                    </div>
                  </div>
                  <div className="klaviyo-col-content">
                    <Card>
                      <div className="card-inner card-inner--center">
                        <img src={ReviewComingSoon} alt="review" />
                        <p className="text-coming-soon">
                          {i18n.translate('AdminHTML.KlaviyoIntegration.Common.commingSoon')}
                        </p>
                        <p>
                          <TextStyle variation="subdued">
                            {i18n.translate('AdminHTML.KlaviyoIntegration.Common.stayTunes')}
                          </TextStyle>
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>

                <hr className="divider" />
              </Layout.Section>
            </Layout>
          </div>

          <ConfirmDisconnectModal
            isOpen={isConfirmDisconnect}
            onConfirm={this.handleDisconnect}
            onClose={this.handleCloseConfirm}
          />

          <PreviewProductsModal
            isOpen={isShowPreview}
            recommendationType={selectedRecommendationType}
            onClose={this.handleClosePreview}
          />
        </Page>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');

  return { selectedPlan };
};
const mapDispatchToProps = (dispatch, ownProps) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(KlaviyoIntegration));
